import { OnInit, Input, OnChanges, SimpleChanges, Component } from '@angular/core';
import { TransactionType, Transaction, TransactionService, TransactionQuery } from '@fgb/core';
import * as moment from 'moment';
import { switchMap, map, tap } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { ViewportScroller } from '@angular/common';

interface GroupedTransactions {
  [day: string]: Transaction[];
}

@Component({
  selector: 'fgb-transaction-list',
  templateUrl: './transaction-list.component.html',
  styleUrls: ['./transaction-list.component.scss'],
})
export class TransactionListComponent implements OnInit, OnChanges {
  constructor(
    private transactionService: TransactionService,
    private transactionQuery: TransactionQuery,
    private scroller: ViewportScroller
  ) { }

  @Input() startDate: Date;
  @Input() endDate: Date;
  @Input() memberId: string;
  @Input() transactionType: TransactionType;
  @Input() dateFormat: string = 'MMM DD, YYYY';
  @Input() ecashView: boolean;
  @Input() accountView: boolean;
  @Input() initialLoad: boolean = true;

  transactions$: Observable<GroupedTransactions>;
  transactionTypes = TransactionType;
  keys = Object.keys;
  loading: boolean = false;
  hasTransactions = false;

  ngOnInit() {
    this.getTransactions();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['startDate'] ||
      changes['endDate'] ||
      changes['memberId'] ||
      changes['transactionType'] ||
      changes['initialLoad']
    ) {
      this.getTransactions();
    }
  }

  getTransactions() {
    this.loading = true;
    const limitToFive = this.initialLoad; // capture before async pipeline runs
    this.transactions$ = this.transactionService
      .fetchTransactions(this.startDate, this.endDate, this.memberId, this.transactionType)
      .pipe(
        switchMap(() =>
          this.transactionQuery.selectTransactions(this.startDate, this.endDate, this.transactionType)
        ),
        map((t: Transaction[]) => this._groupByTransactionDate(t, limitToFive)),
        tap((groupedTransactions) => {
          this.hasTransactions = Object.keys(groupedTransactions).length > 0;
          this.loading = false;
        })
      );
    this.scroller.scrollToAnchor('transactionList');
  }

  private _groupByTransactionDate(transactions: Transaction[], limitToFive = false): GroupedTransactions {
    const sorted = transactions.sort(
      (a, b) => new Date(b.TransactionDateUTC).getTime() - new Date(a.TransactionDateUTC).getTime()
    );

    const list = limitToFive ? sorted.slice(0, 5) : sorted;

    return list.reduce<GroupedTransactions>((grouped, tx) => {
      const txDate = moment(tx.TransactionDateUTC).format(this.dateFormat);
      grouped[txDate] = grouped[txDate] ? [...grouped[txDate], tx] : [tx];
      return grouped;
    }, {});
  }

  keepOrder = (a: any, b: any) => a.key;
}
